/**
 * Homebrew class → SrdClass reconciliation.
 *
 * The homebrew extraction schema (server/src/ai/extraction/schemas/
 * homebrew-schema.ts) and the engine's SrdClass shape have DRIFTED:
 *
 *   extractor emits              engine reads
 *   ───────────────────────────  ──────────────────────────────
 *   armorProficiencies[]         proficiencies[]   (single merged list)
 *   weaponProficiencies[]        │
 *   toolProficiencies[]          ┘
 *   skillChoices {choose,from}   skillChoices[]    (array of SrdSkillChoice)
 *   (no levels[])                levels[].spellcasting.slots
 *
 * Consequence before this mapper: a homebrew class force-cast
 * (`entry.data as unknown as SrdClass`) gave the engine `proficiencies: []`
 * (wrong field name), a non-array `skillChoices` (→ `[0]` undefined in the
 * wizard), and an empty `levels[]` (→ a caster homebrew got ZERO spell slots).
 *
 * This mapper is the SINGLE reconciliation point so every consumer
 * (session-srd `getClasses`, `sheet:level-up`, `sheet:update` slot recompute)
 * sees one consistent SrdClass. Spell slots: the extractor cannot produce a
 * slot table, so it is derived from `data.casterType` (full/half/third/pact)
 * via `singleClassSlots` — the correct single-class progression per level.
 * If `casterType` is absent but `spellcastingAbility` is set, we default to
 * `full` to preserve the previous behavior. pact/none classes get an empty
 * level table (pact magic is a separate resource resolved in computeSpellSlots).
 */
import { singleClassSlots } from '../multiclass-spells.js';
function asStringArray(v) {
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
}
/**
 * Merge the extractor's three proficiency arrays into the engine's flat
 * `proficiencies[]`. If a pre-merged `proficiencies` array is already present
 * (future schema / already-mapped data), prefer it. Deduplicated, order stable.
 */
function mergeProficiencies(data) {
    if (Array.isArray(data.proficiencies))
        return asStringArray(data.proficiencies);
    const merged = [
        ...asStringArray(data.armorProficiencies),
        ...asStringArray(data.weaponProficiencies),
        ...asStringArray(data.toolProficiencies),
    ];
    return [...new Set(merged)];
}
/**
 * Normalize `skillChoices` to the engine's `SrdSkillChoice[]`. The extractor
 * emits a single `{choose, from}` object; the engine and client wizard expect
 * an array (they read `skillChoices[0]`). Accept both shapes.
 */
function normalizeSkillChoices(v) {
    const one = (o) => {
        if (o && typeof o === 'object' && 'choose' in o && 'from' in o) {
            const choose = o.choose;
            const from = o.from;
            if (typeof choose === 'number' && Array.isArray(from)) {
                return { choose, from: from.filter((x) => typeof x === 'string') };
            }
        }
        return null;
    };
    if (Array.isArray(v))
        return v.map(one).filter((x) => x !== null);
    const single = one(v);
    return single ? [single] : [];
}
function asFeatures(v) {
    if (!Array.isArray(v))
        return [];
    return v.flatMap((f) => {
        if (f && typeof f === 'object' && typeof f.name === 'string') {
            const rec = f;
            return [{
                    name: rec.name,
                    level: typeof rec.level === 'number' ? rec.level : 1,
                    description: typeof rec.description === 'string' ? rec.description : '',
                    ...(typeof rec.subclass === 'string' ? { subclass: rec.subclass } : {}),
                }];
        }
        return [];
    });
}
/** Type guard for the `casterType` field emitted by the extractor. */
function isCasterType(v) {
    return v === 'full' || v === 'half' || v === 'third' || v === 'pact' || v === 'none';
}
/**
 * Single-class slot progression for the given caster type, keyed by class level
 * 1-20. full/half/third build a level table via `singleClassSlots`; pact and
 * none have no normal slot table (pact magic is resolved separately in
 * `computeSpellSlots`).
 */
function deriveCasterLevels(casterType) {
    if (casterType === 'pact' || casterType === 'none')
        return [];
    const out = [];
    for (let level = 1; level <= 20; level++) {
        out.push({ level, spellcasting: { slots: singleClassSlots(casterType, level) } });
    }
    return out;
}
function normalizeLevels(v, casterType, spellcastingAbility) {
    if (Array.isArray(v) && v.length > 0)
        return v;
    // Extractor produced no level table. Derive the caster's slots so the sheet
    // engine grants spell slots at creation and level-up; non-casters (and
    // pact-only classes) legitimately have an empty level table.
    return spellcastingAbility ? deriveCasterLevels(casterType) : [];
}
/**
 * Convert a raw homebrew class `entry.data` (loose Json) into the typed
 * SrdClass shape the sheet engine consumes. See file header for the drift this
 * reconciles.
 */
export function homebrewClassDataToSrdClass(slug, name, data) {
    const spellcastingAbility = typeof data.spellcastingAbility === 'string' ? data.spellcastingAbility : undefined;
    // Resolve the caster type carried by the extraction schema (T1). Fallback:
    // if absent but spellcastingAbility is present, default to 'full' to preserve
    // the previous behavior (all casting homebrew got full-caster slots).
    const casterType = isCasterType(data.casterType)
        ? data.casterType
        : spellcastingAbility
            ? 'full'
            : 'none';
    return {
        slug,
        name,
        hitDie: typeof data.hitDie === 'number' ? data.hitDie : 8,
        savingThrows: asStringArray(data.savingThrows),
        proficiencies: mergeProficiencies(data),
        skillChoices: normalizeSkillChoices(data.skillChoices),
        subclass: data.subclass ?? null,
        features: asFeatures(data.features),
        levels: normalizeLevels(data.levels, casterType, spellcastingAbility),
        spellcastingAbility,
        casterType,
    };
}
